import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { X, FileText, Video, Music, Image, Archive, File, ExternalLink, Globe, Upload } from 'lucide-react';
import { DigitalContentFormData, ContentType } from '@/types/digitalContent';

interface ContentDialogsProps {
  isCreateOpen: boolean;
  isEditOpen: boolean;
  onCreateClose: () => void;
  onEditClose: () => void;
  onSubmit: (data: DigitalContentFormData & { coverImageFile?: File }) => void;
  isLoading: boolean;
  editData?: DigitalContentFormData;
}

const CONTENT_TYPES: { value: ContentType; label: string; icon: React.ReactNode; color: string; examples: string[] }[] = [
  { 
    value: 'pdf', 
    label: 'PDF', 
    icon: <FileText className="w-4 h-4" />, 
    color: 'bg-red-500',
    examples: ['Google Drive', 'Dropbox', 'OneDrive', 'Site próprio']
  },
  { 
    value: 'video', 
    label: 'Vídeo', 
    icon: <Video className="w-4 h-4" />, 
    color: 'bg-blue-500',
    examples: ['YouTube', 'Vimeo', 'Google Drive', 'Twitch']
  },
  { 
    value: 'audio', 
    label: 'Áudio', 
    icon: <Music className="w-4 h-4" />, 
    color: 'bg-purple-500',
    examples: ['Spotify', 'SoundCloud', 'Google Drive', 'Anchor']
  },
  { 
    value: 'image', 
    label: 'Imagem', 
    icon: <Image className="w-4 h-4" />, 
    color: 'bg-green-500',
    examples: ['Imgur', 'Google Drive', 'Dropbox', 'Site próprio']
  },
  { 
    value: 'zip', 
    label: 'ZIP', 
    icon: <Archive className="w-4 h-4" />, 
    color: 'bg-yellow-500',
    examples: ['Google Drive', 'Dropbox', 'OneDrive', 'MediaFire']
  },
  { 
    value: 'document', 
    label: 'Documento', 
    icon: <File className="w-4 h-4" />, 
    color: 'bg-gray-500',
    examples: ['Google Docs', 'OneDrive', 'Dropbox', 'Notion']
  },
];

export function ContentDialogs({
  isCreateOpen,
  isEditOpen,
  onCreateClose,
  onEditClose,
  onSubmit,
  isLoading,
  editData
}: ContentDialogsProps) {
  const [formData, setFormData] = useState<DigitalContentFormData>({
    title: '',
    description: '',
    content_type: 'pdf',
    price_cents: 0,
    cover_image_url: '',
    file_url: '',
    file_size_bytes: 0,
    file_name: '',
    preview_content: '',
    tags: [],
    difficulty_level: 'iniciante',
    estimated_duration_minutes: 0,
    is_featured: false,
    game_id: null,
  });

  const [coverImageFile, setCoverImageFile] = useState<File | null>(null);
  const [coverImagePreview, setCoverImagePreview] = useState<string>('');

  // Reset form when dialog opens
  React.useEffect(() => {
    if (isCreateOpen) {
      setFormData({
        title: '',
        description: '',
        content_type: 'pdf',
        price_cents: 0,
        cover_image_url: '',
        file_url: '',
        file_size_bytes: 0,
        file_name: '',
        preview_content: '',
        tags: [],
        difficulty_level: 'iniciante',
        estimated_duration_minutes: 0,
        is_featured: false,
        game_id: null,
      });
      setCoverImageFile(null);
      setCoverImagePreview('');
    } else if (isEditOpen && editData) {
      setFormData(editData);
      setCoverImageFile(null);
      setCoverImagePreview(editData.cover_image_url || '');
    }
  }, [isCreateOpen, isEditOpen, editData]);

  const handleInputChange = (field: keyof DigitalContentFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCoverImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validar se é imagem
      if (!file.type.startsWith('image/')) {
        alert('Por favor, selecione apenas arquivos de imagem.');
        return;
      }

      // Validar tamanho (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        alert('A imagem deve ter no máximo 5MB.');
        return;
      }

      setCoverImageFile(file);
      
      // Criar preview
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setCoverImagePreview(result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeCoverImage = () => {
    setCoverImageFile(null);
    setCoverImagePreview('');
    handleInputChange('cover_image_url', '');
  };

  const validateUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validações básicas
    if (!formData.title.trim()) {
      alert('Título é obrigatório');
      return;
    }
    
    if (!formData.description.trim()) {
      alert('Descrição é obrigatória');
      return;
    }
    
    if (!formData.file_url.trim()) {
      alert('Link do conteúdo é obrigatório');
      return;
    }
    
    if (!validateUrl(formData.file_url)) {
      alert('Por favor, insira um link válido para o conteúdo');
      return;
    }

    // Adicionar arquivo de capa aos dados se existir
    const submitData = {
      ...formData,
      coverImageFile
    };

    onSubmit(submitData);
  };

  const selectedContentType = CONTENT_TYPES.find(type => type.value === formData.content_type);

  const renderDialog = (isOpen: boolean, onClose: () => void, title: string) => (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()} modal>
      <DialogContent 
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {selectedContentType?.icon}
            {title}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Tipo de Conteúdo */}
          <div className="space-y-2">
            <Label htmlFor="content_type">Tipo de Conteúdo</Label>
            <Select
              value={formData.content_type}
              onValueChange={(value: ContentType) => handleInputChange('content_type', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CONTENT_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded ${type.color}`} />
                      {type.icon}
                      {type.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Informações sobre plataformas suportadas */}
            {selectedContentType && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <Globe className="w-4 h-4 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-900">
                      Plataformas recomendadas para {selectedContentType.label}:
                    </p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {selectedContentType.examples.map(example => (
                        <span
                          key={example}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800"
                        >
                          {example}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Título */}
          <div className="space-y-2">
            <Label htmlFor="title">Título *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder="Ex: Guia Completo de League of Legends"
              required
            />
          </div>

          {/* Descrição */}
          <div className="space-y-2">
            <Label htmlFor="description">Descrição *</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Descreva o conteúdo do seu material..."
              rows={4}
              required
            />
          </div>

          {/* Link do Conteúdo */}
          <div className="space-y-2">
            <Label htmlFor="file_url">Link do Conteúdo *</Label>
            <div className="relative">
              <ExternalLink className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <Input
                id="file_url"
                type="url"
                value={formData.file_url}
                onChange={(e) => handleInputChange('file_url', e.target.value)}
                placeholder="https://drive.google.com/... ou https://youtube.com/..."
                className="pl-10"
                required
              />
            </div>
            <p className="text-xs text-gray-500">
              Cole aqui o link direto para seu conteúdo (YouTube, Google Drive, Dropbox, etc.)
            </p>
          </div>

          {/* Upload de Imagem de Capa */}
          <div className="space-y-2">
            <Label>Imagem de Capa</Label>
            {coverImagePreview ? (
              <div className="relative">
                <img
                  src={coverImagePreview}
                  alt="Preview da capa"
                  className="w-full h-48 object-cover rounded-lg border"
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={removeCoverImage}
                  className="absolute top-2 right-2"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleCoverImageUpload}
                  className="hidden"
                  id="cover-upload"
                />
                <label htmlFor="cover-upload" className="cursor-pointer">
                  <div className="flex flex-col items-center gap-2">
                    <Upload className="w-8 h-8 text-gray-400" />
                    <span className="text-sm text-gray-600">
                      Clique para fazer upload da imagem de capa
                    </span>
                    <span className="text-xs text-gray-500">
                      PNG, JPG até 5MB
                    </span>
                  </div>
                </label>
              </div>
            )}
          </div>

          {/* Preço */}
          <div className="space-y-2">
            <Label htmlFor="price">Preço (R$)</Label>
            <Input
              id="price"
              type="number"
              step="0.01"
              min="0"
              value={formData.price_cents / 100}
              onChange={(e) => handleInputChange('price_cents', Math.round(parseFloat(e.target.value || '0') * 100))}
              placeholder="29.99 (deixe 0 para gratuito)"
            />
            <p className="text-xs text-gray-500">
              Digite 0 ou deixe vazio para tornar o conteúdo gratuito
            </p>
          </div>

          {/* Botões */}
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Salvando...' : (isEditOpen ? 'Atualizar' : 'Criar Conteúdo')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );

  return (
    <>
      {renderDialog(isCreateOpen, onCreateClose, 'Criar Novo Conteúdo')}
      {renderDialog(isEditOpen, onEditClose, 'Editar Conteúdo')}
    </>
  );
} 